import React, { useState, useEffect, useRef } from 'react';
import Markdown from 'markdown-to-jsx';
import DocumentationSidebar from '@app/components/documentation/docSidebar';
import Page from '@app/components/Page';
import { Col, Row, Alert } from 'reactstrap';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import getDocsStructure from '@app/utils/getDocsStructure';
import { Link, useHistory } from 'react-router-dom';
import { MdSchool } from 'react-icons/md';
import bn from '@app/utils/bemnames';
import Typography from '@app/components/Typography';
import './DocumentationView.css';

// Error boundary component for markdown parsing
class MarkdownErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Markdown parsing error:', error, errorInfo);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert color="danger" fade={false}>
          <strong>Markdown Parsing Error:</strong> There was an error parsing this document. 
          This is likely due to malformed table syntax or other markdown formatting issues.
          <br />
          <small>Technical details: {this.state.error?.message}</small>
        </Alert>
      );
    }

    return this.props.children;
  }
}

// Force include the CSS with a style tag
const styleTag = `
  .documentation h1, 
  .documentation h2, 
  .documentation h3, 
  .documentation .breadcrumb, 
  .documentation__inline-link, 
  .documentation__doc-link {
    font-family: 'Gotham Bold', sans-serif !important;
  }
`;

//@ts-ignore
const context = require.context('@app/pages/documentation/docs', true, /\.md$/);

const bem = bn.create('documentation');

const DocumentationView = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [content, setContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [docsStructure, setDocsStructure] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  const { theme } = React.useContext(ThemeContext);
  const history = useHistory();
  const contentRef = useRef(null);

  const searchRef = useRef(null);

  // Clear debug info when successfully loading a document
  useEffect(() => {
    if (selectedDoc) {
      setDebugInfo('');
    }
  }, [selectedDoc]);

  // Fix special links after the component has rendered
  useEffect(() => {
    if (contentRef.current && selectedDoc) {
      // Find all text nodes that contain the pattern 'see [here]'
      const processTextNodes = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          // Check for different patterns of [here] links
          if (text && (
              text.includes('[here]') || 
              text.includes('see [here]') || 
              text.includes('See [here]') ||
              text.includes('visit [here]') ||
              text.includes('Visit [here]')
            )) {
              
            // Split using a more general regex that catches multiple patterns
            // We need to keep the text structure intact, just replace [here] links
            // This regex captures URLs with fragments (#something) as well
            const parts = text.split(/(\[here\]\(\/docs\/[^)]+(?:#[^)]+)?\))/g);
            
            if (parts.length > 1) {
              const fragment = document.createDocumentFragment();
              
              parts.forEach(part => {
                // Check if this part contains a [here] link
                if (part.includes('[here]')) {
                  // Extract the link URL
                  const urlMatch = part.match(/\[here\]\((\/docs\/[^)]+(?:#[^)]+)?)\)/);
                  if (urlMatch && urlMatch[1]) {
                    const url = urlMatch[1];
                    
                    // Create the link
                    const link = document.createElement('a');
                    link.textContent = 'here';
                    link.href = '#';
                    link.className = bem.e('inline-link');
                    
                    // Add click handler
                    link.addEventListener('click', (e) => {
                      e.preventDefault();
                      
                      // Handle specific CloudAccess Specifications links
                      if (url.includes('CloudAccess Specifications/edge')) {
                        handleSelect('4 CloudAccess Specifications/1 Edge Device Specifications.md');
                      } else if (url.includes('CloudAccess Specifications/controller')) {
                        handleSelect('4 CloudAccess Specifications/2 Controller Specifications.md');
                      } else if (url.includes('intro/topology')) {
                        handleSelect('1 Introduction/4 CloudAccess Network Topology.md');
                      } else if (url.includes('CloudAccess Specifications/routing')) {
                        handleSelect('4 CloudAccess Specifications/3 Bonding Requirements.md');
                      } else if (url.includes('capulse/intro')) {
                        // Handle links to CAPulse intro, even with fragments
                        handleSelect('3 CAPulse/1 Introduction To CAPulse.md');
                      } else {
                        // Try to find matching document
                        const matchingDoc = findBestMatchingDoc(url);
                        if (matchingDoc) {
                          handleSelect(matchingDoc);
                        } else {
                          console.error("Could not find matching document for", url);
                          setDebugInfo(`No match for: ${url}`);
                        }
                      }
                    });
                    
                    fragment.appendChild(link);
                  } else {
                    // If we couldn't parse the URL, just add the text
                    const textNode = document.createTextNode(part);
                    fragment.appendChild(textNode);
                  }
                } else {
                  // This is regular text
                  const textNode = document.createTextNode(part);
                  fragment.appendChild(textNode);
                }
              });
              
              // Replace the original node with our fragment
              node.parentNode.replaceChild(fragment, node);
              return true; // Signal that we modified the DOM
            }
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Process child nodes
          const childNodes = Array.from(node.childNodes);
          let modified = false;
          
          for (const child of childNodes) {
            modified = processTextNodes(child) || modified;
          }
          
          return modified;
        }
        
        return false;
      };
      
      // Process all text nodes in the content area
      processTextNodes(contentRef.current);
    }
  }, [selectedDoc, content]);

  const extractFileName = (filePath) => {
    return filePath.replace(/^.*\/\d*\s*(.*?)\.md$/, '$1');
  }

  useEffect(() => {
    setDocsStructure(getDocsStructure());
  }, []);

  useEffect(() => {
    if (docsStructure && Object.keys(docsStructure).length > 0 && !selectedDoc) {
      try {
        // Default to the Adopting SDN file
        const introPath = '1 Getting Started/1 Adopting SDN.md';
        const mdContent = context(`./${introPath}`);
        setContent(mdContent.default || mdContent);
        setSelectedDoc(introPath);
      } catch (error) {
        console.error('Error loading default document:', error);
      }
    }
  }, [docsStructure, selectedDoc]);

  const handleSelect = (docPath) => {
    try {
      const mdContent = context(`./${docPath}`);
      const content = mdContent.default || mdContent;
      // Pre-process content to fix common table issues
      // First normalize line endings
      let cleanedContent = content
        .replace(/\r\n/g, '\n') // Normalize Windows line endings
        .replace(/\r/g, '\n');  // Normalize old Mac line endings
      
      // Custom table parser - convert to a special format that we can intercept
      const parseMarkdownTables = (content) => {
        const lines = content.split('\n');
        const result = [];
        let i = 0;
        
        while (i < lines.length) {
          const line = lines[i];
          
          // Check if this line starts a table (has pipes)
          if (line.includes('|') && line.trim() !== '') {
            // Look ahead to see if next line is a separator
            const nextLine = lines[i + 1];
            if (nextLine && nextLine.includes('|') && nextLine.includes('-')) {
              // This is a table! Parse it
              const tableLines = [];
              let j = i;
              
              // Collect all table lines
              while (j < lines.length && lines[j].includes('|') && lines[j].trim() !== '') {
                tableLines.push(lines[j]);
                j++;
              }
              
              // Convert to a special marker that we can replace later
              const tableId = `TABLE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              result.push(`[CUSTOM_TABLE:${tableId}]`);
              
              // Store table data for later processing
              if (!window.customTables) window.customTables = {};
              window.customTables[tableId] = tableLines;
              
              i = j; // Skip processed lines
              continue;
            }
          }
          
          result.push(line);
          i++;
        }
        
        return result.join('\n');
      };
      
      // Apply custom table parsing
      cleanedContent = parseMarkdownTables(cleanedContent);
      
      
      setContent(cleanedContent);
      setSelectedDoc(docPath);
      setDebugInfo('');
    } catch (error) {
      console.error('Error loading document:', error);
      setDebugInfo(`Failed to load: ${docPath}. Error: ${(error as Error).message || 'Unknown error'}`);
    }
    setShowDropdown(false);
    setSearchQuery('');
  };

  // Function to find the best matching document for a path
  const findBestMatchingDoc = (path) => {
    const allDocs = context.keys().map(key => key.replace('./', ''));
    
    // Clean the path - remove any URL fragments (#something)
    let cleanPath = path.replace('/docs/', '');
    const hasFragment = cleanPath.includes('#');
    let fragment = '';
    
    if (hasFragment) {
      // Store the fragment for potential use
      const parts = cleanPath.split('#');
      cleanPath = parts[0];
      fragment = parts[1];
    }
    
    // Handle CAPulse links
    if (path.includes('/docs/capulse/intro')) {
      const capulseDoc = allDocs.find(doc => 
        doc.includes('CAPulse') && 
        doc.includes('Introduction To CAPulse')
      );
      
      if (capulseDoc) {
        return capulseDoc;
      }
    }
    
    // Special handling for Adding Users section in CAPulse
    if (fragment === 'user' && path.includes('/docs/capulse/')) {
      const userDoc = allDocs.find(doc =>
        doc.includes('CAPulse') &&
        doc.includes('Adding Users')
      );
      
      if (userDoc) {
        return userDoc;
      }
    }
    
    // Special case handling for specific links
    if (path.includes('/docs/CloudAccess Specifications/edge') || 
        cleanPath.includes('CloudAccess Specifications/edge')) {
      // This is the specific link from the screenshots
      // Specifically look for the Edge Device Specifications document
      const specDoc = allDocs.find(doc => 
        doc.includes('CloudAccess Specifications') && 
        (doc.includes('Edge Device') || doc.includes('Edge'))
      );
      
      if (specDoc) {
        return specDoc;
      }
    }
    
    // Handle the controller specifications link
    if (path.includes('/docs/CloudAccess Specifications/controller') || 
        cleanPath.includes('CloudAccess Specifications/controller')) {
      const controllerDoc = allDocs.find(doc => 
        doc.includes('CloudAccess Specifications') && 
        doc.includes('Controller Specifications')
      );
      
      if (controllerDoc) {
        return controllerDoc;
      }
    }
    
    // Handle the routing link
    if (path.includes('/docs/CloudAccess Specifications/routing')) {
      const routingDoc = allDocs.find(doc => 
        doc.includes('CloudAccess Specifications') && 
        doc.includes('Bonding')
      );
      
      if (routingDoc) {
        return routingDoc;
      }
    }
    
    // Handle topology links
    if (path.includes('/docs/intro/topology')) {
      const topologyDoc = allDocs.find(doc => 
        doc.includes('Introduction') && doc.includes('Network Topology')
      );
      
      if (topologyDoc) {
        return topologyDoc;
      }
    }
    
    // Generic handler for CloudAccess Specifications
    if (cleanPath.includes('CloudAccess Specifications')) {
      // Look for any CloudAccess Specifications document
      const specDoc = allDocs.find(doc => 
        doc.includes('CloudAccess Specifications') && doc.endsWith('.md')
      );
      
      if (specDoc) {
        return specDoc;
      }
      
      // Fallback to a hardcoded path if nothing else matched
      cleanPath = '4 CloudAccess Specifications/1 Edge Device Specifications.md';
      return cleanPath;
    }
    
    // Try exact match first
    const exactMatch = allDocs.find(doc => 
      doc.toLowerCase() === cleanPath.toLowerCase() ||
      doc.toLowerCase() === `${cleanPath}.md`.toLowerCase()
    );
    
    if (exactMatch) {
      return exactMatch;
    }
    
    // Try to match by containing the path
    const containsMatch = allDocs.find(doc => 
      doc.toLowerCase().includes(cleanPath.toLowerCase())
    );
    
    if (containsMatch) {
      return containsMatch;
    }
    
    // Try to match by last segment of path
    const segments = cleanPath.split('/');
    if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1];
      const segmentMatch = allDocs.find(doc => 
        doc.toLowerCase().includes(lastSegment.toLowerCase())
      );
      
      if (segmentMatch) {
        return segmentMatch;
      }
    }
    
    return null;
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
  
    const lowerCaseQuery = searchQuery.toLowerCase();
    const results = new Map();
  
    context.keys().forEach((filePath) => {
      const fileName = filePath.replace('./', '');
      const mdContent = context(filePath).default || context(filePath);
      let foundTitle = extractFileName(fileName);
  
      const lines = mdContent.split('\n');
      for (const line of lines) {
        if (line.startsWith('#')) {
          foundTitle = line.replace(/^#+\s*/, '');
        }
        
        if (line.toLowerCase().includes(lowerCaseQuery)) {
          results.set(fileName, { path: fileName, title: foundTitle });
          break;
        }
      }
    });
  
    const finalResults = Array.from(results.values());
  
    setSearchResults(finalResults);
    setShowDropdown(finalResults.length > 0);
  }, [searchQuery]);
  

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Custom table component
  const CustomTable = ({ tableId }) => {
    const tableData = window.customTables?.[tableId];
    if (!tableData || tableData.length < 2) {
      return <div>Table not found</div>;
    }
    
    const headerLine = tableData[0];
    const dataLines = tableData.slice(2); // Skip separator line
    
    // Parse header
    const headers = headerLine.split('|').map(h => h.trim()).filter(h => h !== '');
    
    const { theme } = React.useContext(ThemeContext);
    const isDark = theme === 'dark';
    
    return (
      <div style={{ overflowX: 'auto', width: '100%', marginBottom: '1rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={index} style={{ 
                  backgroundColor: isDark ? '#222020' : '#f8f9fa', 
                  border: `1px solid ${isDark ? '#444' : '#dee2e6'}`, 
                  padding: '0.75rem', 
                  textAlign: 'left', 
                  fontWeight: 'bold', 
                  verticalAlign: 'top',
                  color: isDark ? '#fff' : 'inherit'
                }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataLines.map((line, rowIndex) => {
              const cells = line.split('|').map(c => c.trim()).filter(c => c !== '');
              if (cells.length === 0) return null;
              
              return (
                <tr key={rowIndex} style={{
                  backgroundColor: rowIndex % 2 === 1 ? (isDark ? '#2c2a2a' : 'rgba(0,0,0,.05)') : 'transparent'
                }}>
                  {cells.map((cell, cellIndex) => (
                    <td key={cellIndex} style={{ 
                      border: `1px solid ${isDark ? '#444' : '#dee2e6'}`, 
                      padding: '0.75rem', 
                      verticalAlign: 'top',
                      color: isDark ? '#fff' : 'inherit'
                    }}>
                      {cell}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Custom components for markdown rendering
  const markdownOverrides = {
    h1: {
      component: ({ children, ...props }) => (
      <h1 style={{ fontFamily: "'Gotham Bold', sans-serif", fontSize: '1.75rem', fontWeight: 500 }} {...props}>
        {children}
      </h1>
      ),
    },
    h2: {
      component: ({ children, ...props }) => (
      <h2 style={{ fontFamily: "'Gotham Bold', sans-serif", fontSize: '1.5rem', fontWeight: 500 }} {...props}>
        {children}
      </h2>
      ),
    },
    h3: {
      component: ({ children, ...props }) => (
      <h3 style={{ fontFamily: "'Gotham Bold', sans-serif", fontSize: '1.25rem', fontWeight: 500 }} {...props}>
        {children}
      </h3>
      ),
    },
    p: {
      component: ({ children, ...props }) => {
      // Check if this paragraph contains a table marker
      const childrenText = React.Children.toArray(children).join('');
      if (childrenText.includes('[CUSTOM_TABLE:')) {
        const tableMatch = childrenText.match(/\[CUSTOM_TABLE:(.*?)\]/);
        if (tableMatch) {
          const tableId = tableMatch[1];
          return <CustomTable tableId={tableId} />;
        }
      }
      
      return (
        <p style={{ fontSize: '14px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }} {...props}>
          {children}
        </p>
      );
    },
    },
    ul: {
      component: ({ children, ...props }) => (
      <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }} {...props}>
        {children}
      </ul>
      ),
    },
    ol: {
      component: ({ children, ...props }) => (
      <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }} {...props}>
        {children}
      </ol>
      ),
    },
    li: {
      component: ({ children, ...props }) => (
      <li style={{ marginBottom: '0.5rem', fontSize: '14px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }} {...props}>
        {children}
      </li>
      ),
    },
    // Table components - with error handling
    table: {
      component: ({ children, ...props }: any) => {
      try {
        const { theme } = React.useContext(ThemeContext);
        return (
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ width: '100%', marginBottom: '1rem', borderCollapse: 'collapse', fontSize: '14px' }} {...props}>
              {children}
            </table>
          </div>
        );
      } catch (error) {
        console.error('Table rendering error:', error);
        return <div>Error rendering table</div>;
      }
    },
    },
    thead: {
      component: ({ children, ...props }: any) => (
      <thead {...props}>
        {children}
      </thead>
      ),
    },
    tbody: {
      component: ({ children, ...props }: any) => (
      <tbody {...props}>
        {children}
      </tbody>
      ),
    },
    tr: {
      component: ({ children, ...props }: any) => {
      const { theme } = React.useContext(ThemeContext);
      const isDark = theme === 'dark';
      
      // Get the row index from the props if available for striping
      const rowIndex = props?.index || props?.['data-index'] || 0;
      const isOdd = rowIndex % 2 === 1;
      
      return (
        <tr 
          style={{
            backgroundColor: isOdd ? (isDark ? '#2c2a2a' : 'rgba(0,0,0,.05)') : 'transparent'
          }}
          {...props}
        >
          {children}
        </tr>
      );
    },
    },
    th: {
      component: ({ children, ...props }: any) => {
      const { theme } = React.useContext(ThemeContext);
      const isDark = theme === 'dark';
      return (
        <th 
          style={{ 
            backgroundColor: isDark ? '#222020' : '#f8f9fa', 
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`, 
            padding: '0.75rem', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            verticalAlign: 'top',
            color: isDark ? '#fff' : 'inherit'
          }} 
          {...props}
        >
          {children}
        </th>
      );
    },
    },
    td: {
      component: ({ children, ...props }: any) => {
      const { theme } = React.useContext(ThemeContext);
      const isDark = theme === 'dark';
      return (
        <td 
          style={{ 
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`, 
            padding: '0.75rem', 
            verticalAlign: 'top',
            color: isDark ? '#fff' : 'inherit'
          }} 
          {...props}
        >
          {children}
        </td>
      );
    },
    },
    a: {
      component: (props) => {
      const { href, children, ...rest } = props;
      
      // Extract the text content regardless of nesting
      const getTextContent = (children) => {
        if (typeof children === 'string') return children;
        if (Array.isArray(children)) {
          return children.map(child => getTextContent(child)).join('');
        }
        if (children && children.props && children.props.children) {
          return getTextContent(children.props.children);
        }
        return '';
      };
      
      const linkText = getTextContent(children);
      
      // Check if the current link is a markdown link with "here" as the text
      const isHereLink = linkText.toLowerCase() === 'here' && href && href.includes('/docs/');
      
      if (isHereLink) {
        // Handle CloudAccess Specifications/edge links specifically
        if (href.includes('CloudAccess Specifications/edge')) {
          // Use a hardcoded path to the Edge Device Specifications document
          const targetDoc = '4 CloudAccess Specifications/1 Edge Device Specifications.md';
          
          return (
            <a 
              onClick={(e) => {
                e.preventDefault();
                handleSelect(targetDoc);
              }}
              href="#"
              className={bem.e('inline-link')}
              style={{ 
                fontFamily: "'Gotham Bold', sans-serif", 
                color: '#85ba3b', 
                fontWeight: 500, 
                textDecoration: 'underline', 
                cursor: 'pointer' 
              }}
              {...rest}
            >
              {children}
            </a>
          );
        }
        
        // For other links, try to find a matching document
        const matchingDoc = findBestMatchingDoc(href);
        if (matchingDoc) {
          return (
            <a 
              onClick={(e) => {
                e.preventDefault();
                handleSelect(matchingDoc);
              }}
              href="#"
              className={bem.e('inline-link')}
              style={{ 
                fontFamily: "'Gotham Bold', sans-serif", 
                color: '#85ba3b', 
                fontWeight: 500, 
                textDecoration: 'underline', 
                cursor: 'pointer' 
              }}
              {...rest}
            >
              {children}
            </a>
          );
        }
      }
      
      // If it's an external link (starts with http or https), use regular anchor
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        return (
          <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer" 
            className={bem.e('external-link')}
            style={{ 
              fontFamily: "'Gotham Bold', sans-serif", 
              color: '#007bff', 
              fontWeight: 500 
            }}
            {...rest}
          >
            {children}
          </a>
        );
      }

      // For internal links, try to find a matching doc
      try {
        if (href) {
          // First check if it's a docs link
          if (href.startsWith('/docs/') || href.includes('/docs/')) {
            const matchingDoc = findBestMatchingDoc(href);
            
            if (matchingDoc) {
              return (
                <a 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelect(matchingDoc);
                  }}
                  href="#"
                  className={bem.e('doc-link')}
                  style={{ 
                    fontFamily: "'Gotham Bold', sans-serif", 
                    color: '#85ba3b', 
                    fontWeight: 500, 
                    cursor: 'pointer' 
                  }}
                  {...rest}
                >
                  {children}
                </a>
              );
            } else {
              setDebugInfo(`No match for: ${href}`);
              // Fallback to a dummy link that shows an error
              return (
                <a 
                  onClick={(e) => {
                    e.preventDefault();
                    alert(`Document not found: ${href}`);
                  }}
                  href="#"
                  className={bem.e('error-link')}
                  style={{ 
                    fontFamily: "'Gotham Bold', sans-serif", 
                    color: '#dc3545', 
                    fontWeight: 500, 
                    cursor: 'pointer' 
                  }}
                  {...rest}
                >
                  {children}
                </a>
              );
            }
          }
        }
        
        // For other internal links, try to use React Router
        return (
          <a 
            onClick={(e) => {
              e.preventDefault();
              if (href) {
                history.push(href);
              }
            }}
            href="#"
            className={bem.e('router-link')}
            style={{ 
              fontFamily: "'Gotham Bold', sans-serif", 
              color: '#371e45', 
              fontWeight: 500, 
              cursor: 'pointer' 
            }}
            {...rest}
          >
            {children}
          </a>
        );
      } catch (error) {
        // Fallback to regular anchor
        return (
          <a 
            href={href} 
            {...rest} 
            className={bem.e('fallback-link')}
            style={{ 
              fontFamily: "'Gotham Bold', sans-serif", 
              color: '#fd7e14', 
              fontWeight: 500 
            }}
          >
            {children}
          </a>
        );
      }
    },
    },
    // Add custom image component to fix image paths
    img: {
      component: (props) => {
      const { src, alt, ...rest } = props;
      
      // If it's an absolute URL or data URL, use it as is
      if (src && (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('data:'))) {
        return <img src={src} alt={alt || ''} className={bem.e('image')} {...rest} />;
      }
      
      // Handle paths that start with /docs/img
      if (src && src.startsWith('/docs/img/')) {
        // Map to the new images directory where we copied the files
        const adjustedSrc = src.replace('/docs/img/', '/images/docs/');
        return <img src={adjustedSrc} alt={alt || ''} className={bem.e('image')} {...rest} onError={(e) => handleImageError(e)} />;
      }
      
      // For other images, try to resolve them relative to the current path
      return <img 
        src={src} 
        alt={alt || ''} 
        className={bem.e('image')}
        {...rest} 
        onError={(e) => handleImageError(e)} 
      />;
    }
    }
  };

  // Helper function to handle image loading errors
  const handleImageError = (e) => {
    const imgElement = e.target as HTMLImageElement;
    
    // Try to find the image in other locations
    if (imgElement.src.includes('/images/docs/')) {
      // We've already tried the main location, show fallback
      showImageFallback(imgElement);
    } else if (imgElement.src.includes('/static/img/')) {
      // If we already tried the static path, try the images path
      const originalPath = imgElement.src.replace(window.location.origin, '');
      const alternatePath = originalPath.replace('/static/img/', '/images/docs/');
      imgElement.src = alternatePath;
      
      // Add a one-time event listener to handle if this attempt also fails
      imgElement.addEventListener('error', () => {
        showImageFallback(imgElement);
      }, { once: true });
    } else if (imgElement.src.includes('/assets/img/')) {
      // If we've already tried the assets path, try the images path
      const originalPath = imgElement.src.replace(window.location.origin, '');
      const alternatePath = originalPath.replace('/assets/img/', '/images/docs/');
      imgElement.src = alternatePath;
      
      // Add a one-time event listener to handle if this attempt also fails
      imgElement.addEventListener('error', () => {
        showImageFallback(imgElement);
      }, { once: true });
    } else {
      // For other paths, try the images path
      const originalPath = imgElement.src.replace(window.location.origin, '');
      if (originalPath.includes('/docs/img/')) {
        const imagesPath = originalPath.replace('/docs/img/', '/images/docs/');
        imgElement.src = imagesPath;
        
        // Add a one-time event listener to handle if this attempt also fails
        imgElement.addEventListener('error', () => {
          showImageFallback(imgElement);
        }, { once: true });
      } else {
        showImageFallback(imgElement);
      }
    }
  };

  // Show a fallback for images that can't be found
  const showImageFallback = (imgElement: HTMLImageElement) => {
    imgElement.classList.add(bem.e('image-error'));
    // Base64 encoded SVG of a "broken image" icon
    imgElement.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmZmVlZWUiIC8+CiAgPHRleHQgeD0iNTAiIHk9IjUwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNkZDAwMDAiPkltYWdlIG5vdCBmb3VuZDwvdGV4dD4KPC9zdmc+';
  };

  return (
    <Page
      tag="div"
      className={bem.b()}
      title=""
      breadcrumbs={[{ name: 'Documentation', active: true }]}
      actionButton={null}
    >
      <style dangerouslySetInnerHTML={{ __html: styleTag }} />
      <Row>
        <Col>
          <div className={bem.e('container')}>
            <Row className="w-100">
              <Col lg={3} md={3} sm={12} xs={12}>
                <div ref={searchRef} className={bem.e('search-container')}>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`form-control mb-2 ${bem.e('search-input')}`}
                    onFocus={() => setShowDropdown(searchResults.length > 0)}
                  />

                  {showDropdown && (
                    <ul className={bem.e('search-results')}>
                      {searchResults.map((result, index) => (
                        <li
                          key={index}
                          className={bem.e('search-result-item')}
                          onClick={() => handleSelect(result.path)}
                        >
                          {result.title}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <DocumentationSidebar onSelect={handleSelect} docs={docsStructure} />
              </Col>
              <Col lg={9} md={9} sm={12} xs={12}>
                {debugInfo && (
                  <Alert color="warning" className={bem.e('alert')}>
                    {debugInfo.startsWith('No match for:') ? (
                      <>
                        <strong>Link Issue:</strong> The documentation link could not be resolved.
                        <div className={bem.e('alert-details')}>
                          Technical details: {debugInfo}
                        </div>
                      </>
                    ) : (
                      debugInfo
                    )}
                  </Alert>
                )}
                <main 
                  ref={contentRef}
                  className={bem.e('content')}
                >
                  {selectedDoc ? (
                    <MarkdownErrorBoundary>
                      <Markdown
                        options={{
                          overrides: markdownOverrides,
                          wrapper: React.Fragment,
                        }}
                      >
                        {content}
                      </Markdown>
                    </MarkdownErrorBoundary>
                  ) : (
                    <p className="text-muted">Select a document to view</p>
                  )}
                </main>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Page>
  );
};

export default DocumentationView;
